// Work orders on the marina's own equipment (Travelifts, forklifts, launches…).
// An operation on a unit has no customer: Customer_ID 'equipment', Customer_Name
// 'Equipment', Category 'Internal', the unit as the boat (Boat_ID equipment-{id},
// Boat_Name its name) and Equipment_Unit {id} (indexed) — that's how the unit's page
// finds its work history, and how the work order links back to the unit's page.
// Made from a service-schedule item it also has Equipment_Service {key}: finishing the
// operation logs that service (functions/index.js clearEquipmentFlagOnDone).
// "Schedule it now" (scheduleAssist.js) puts it on a tech's Scheduler row with a start
// day, the same way the Work Orders add-operation forms do.
// "Do not use until fixed" puts a dnu defect on the unit pointing at the operation
// (defect.job); the clearEquipmentFlagOnDone Cloud Function resolves it when the
// operation is finished.

import { auth, database } from './initFirebase';
import { ref, get, query, orderByChild, equalTo, runTransaction, push as newKey } from 'firebase/database';
import { update } from './trackedDb'; // durable offline writes
import { escapeHTML } from './utilities';
import { todayYMD } from './expiryDates';
import { mountScheduleAssist } from './scheduleAssist';

const esc = v => escapeHTML(v == null ? '' : String(v));
export const EQUIPMENT_CUSTOMER = 'equipment';
const CLOSED = ['Closed', 'Declined', 'Ready to Bill', 'Complete'];
export const INTERNAL_CATEGORY = 'Internal';

const actor = () => { try { return localStorage.getItem('currentUserName') || 'Office'; } catch { return 'Office'; } };
const shortDay = ds => new Date(`${ds}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const ymd = ms => { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/** Every operation ever opened on a unit (open, finished and closed), from /data. */
export async function loadUnitOps(unitId) {
  const snap = await get(query(ref(database, 'data'), orderByChild('Equipment_Unit'), equalTo(unitId)));
  return Object.entries(snap.val() || {}).map(([id, op]) => ({ id, ...op })).filter(op => op && op.Status !== 'Declined');
}

/** The unit's open work orders: { 'WO-2224': [ops] }. */
export async function openWorkOrders(unitId) {
  const open = {};
  (await loadUnitOps(unitId)).forEach(op => {
    if (!op.Work_Order || CLOSED.includes(op.Status)) return;
    (open[op.Work_Order] = open[op.Work_Order] || []).push(op);
  });
  return open;
}

let current = null; // { opts }
let assist = null;  // the dialog's "Schedule it now" picker

/**
 * The Make work order dialog.
 * opts: { unitId, unitName, sub, what, notes, hours?, service?: {key, label}, photos: {key: path},
 *         launchCheck?: {fix, run, item, kind},
 *         extraPatch?: ({ workOrder, opId, dnuKey, by, now }) => patch, toast, onDone? }
 */
export async function openWorkOrderDialog(opts) {
  current = opts;
  const dlg = ensureDialog();
  dlg.querySelector('[name=what]').value = opts.what || '';
  dlg.querySelector('[name=notes]').value = opts.notes || '';
  dlg.querySelector('[name=priority]').value = 'Normal';
  dlg.querySelector('[name=hours]').value = opts.hours > 0 ? String(opts.hours) : '';
  dlg.querySelector('[name=dnu]').checked = false;
  dlg.querySelector('.lco-dlg-sub').textContent = opts.sub || opts.unitName || opts.unitId;
  const target = dlg.querySelector('.lco-dlg-target');
  target.innerHTML = '<label><input type="radio" name="target" value="new" checked> New work order</label>';
  dlg.querySelector('.lco-dlg-go').disabled = false;
  assist.reset();
  assist.refresh();
  dlg.showModal();
  dlg.querySelector('[name=what]').focus();
  try {
    const open = await openWorkOrders(opts.unitId);
    if (current !== opts) return;
    target.insertAdjacentHTML('beforeend', Object.entries(open).map(([wo, ops]) => `<label><input type="radio" name="target" value="${esc(wo)}"> Add to ${esc(wo)} <span class="lco-hint">${ops.length} open operation${ops.length === 1 ? '' : 's'}: ${esc(ops.map(o => o.Short_Description).join(', ').slice(0, 80))}</span></label>`).join(''));
  } catch (e) { console.warn('[equipmentWork] open work orders', e); }
}

function ensureDialog() {
  let dlg = document.getElementById('lco-wo-dialog');
  if (dlg) return dlg;
  dlg = document.createElement('dialog');
  dlg.id = 'lco-wo-dialog';
  dlg.className = 'lco-dlg';
  dlg.innerHTML = `<form method="dialog" class="lco-dlg-form">
    <div class="lco-dlg-head"><div><div class="lco-dlg-title">Make work order</div><div class="lco-dlg-sub"></div></div><button type="button" class="eq-x" data-dlg-close aria-label="Close">×</button></div>
    <label class="eq-field"><span>Operation</span><input class="eq-input" name="what" maxlength="120" required></label>
    <label class="eq-field"><span>Notes for the tech</span><textarea class="eq-input" name="notes" rows="3" maxlength="1000"></textarea></label>
    <div class="eq-grid2">
      <label class="eq-field"><span>Priority</span><select class="eq-input" name="priority"><option>Normal</option><option>High</option></select></label>
      <label class="eq-field"><span>Estimated hours</span><input class="eq-input" name="hours" type="number" min="0" step="0.5" placeholder="needed to schedule"></label>
    </div>
    <div class="schedule-assist hidden" data-sa></div>
    <div class="eq-field"><span class="eq-label">Where</span><div class="lco-dlg-target"></div></div>
    <label class="lco-dlg-check"><input type="checkbox" name="dnu"> Flag the unit <b>Do not use</b> until this is finished</label>
    <div class="lco-dlg-foot"><span class="lco-hint">The work order is on the unit itself and shows in its work history. The flag clears when the operation is finished.</span><button type="button" class="eq-btn" data-dlg-close>Cancel</button><button type="button" class="eq-btn eq-btn-primary lco-dlg-go">Create</button></div>
  </form>`;
  document.body.appendChild(dlg);
  dlg.addEventListener('click', e => { if (e.target.closest('[data-dlg-close]')) dlg.close(); });
  dlg.querySelector('.lco-dlg-go').addEventListener('click', () => create(dlg));
  assist = mountScheduleAssist({
    box: dlg.querySelector('[data-sa]'),
    categories: [INTERNAL_CATEGORY],
    getDraft: () => ({
      category: INTERNAL_CATEGORY,
      hours: dlg.querySelector('[name=hours]').value,
      description: dlg.querySelector('[name=notes]').value,
      shortDesc: dlg.querySelector('[name=what]').value,
    }),
  });
  dlg.querySelector('[name=hours]').addEventListener('input', () => assist.refreshSoon());
  return dlg;
}

async function create(dlg) {
  const o = current;
  const what = dlg.querySelector('[name=what]').value.trim();
  if (!o || !what) { dlg.querySelector('[name=what]').focus(); return; }
  const go = dlg.querySelector('.lco-dlg-go');
  go.disabled = true;
  try {
    const target = dlg.querySelector('[name=target]:checked')?.value || 'new';
    const workOrder = target === 'new'
      ? `WO-${(await runTransaction(ref(database, 'meta/nextWorkOrderSeq'), c => (c || 2200) + 1)).snapshot.val()}`
      : target;
    const today = todayYMD();
    const now = Date.now();
    const by = actor();
    const hours = parseFloat(dlg.querySelector('[name=hours]').value);
    const dnu = dlg.querySelector('[name=dnu]').checked;
    const opId = newKey(ref(database, 'data')).key;
    const pick = hours > 0 ? assist.selection() : null; // { tech, start, end }
    const photos = {};
    Object.entries(o.photos || {}).forEach(([k, path]) => { photos[k] = { note: o.launchCheck ? 'Launch check' : '', path }; });
    const op = {
      id: opId, Job_ID: opId, Work_Order: workOrder,
      Customer_ID: EQUIPMENT_CUSTOMER, Customer_Name: 'Equipment',
      Boat_ID: `equipment-${o.unitId}`, Boat_Name: o.unitName || o.unitId, Equipment_Unit: o.unitId,
      Category: INTERNAL_CATEGORY, Short_Description: what, Description: dlg.querySelector('[name=notes]').value.trim(),
      Status: 'On Work Schedule', Priority: dlg.querySelector('[name=priority]').value,
      Start_Date: today, Request_Date: today, Charge: '0.00', Techs: pick ? pick.tech : '',
      ...(pick ? { Schedule_Date: pick.start } : {}),
      ...(o.service?.key ? { Equipment_Service: o.service.key } : {}),
      ...(hours > 0 ? { Estimated_Hours: String(hours) } : {}),
      ...(Object.keys(photos).length ? { Photos: photos } : {}),
      ...(o.launchCheck ? { Launch_Check: o.launchCheck } : {}),
    };
    const patch = { [`data/${opId}`]: op, 'data/lastUpdated': now };
    const hKey = newKey(ref(database, `equipment/units/${o.unitId}/history`)).key;
    patch[`equipment/units/${o.unitId}/history/${hKey}`] = { date: today, kind: 'note', text: `${workOrder}: ${what}`, meta: `${by}${o.launchCheck ? ' · from launch check' : ''}`, uid: auth.currentUser?.uid || '', at: now };
    let dnuKey = null;
    if (dnu) {
      dnuKey = newKey(ref(database, `equipment/units/${o.unitId}/defects`)).key;
      patch[`equipment/units/${o.unitId}/defects/${dnuKey}`] = {
        sev: 'dnu', title: what, detail: [o.note, `Work order ${workOrder} — clears when it's finished`].filter(Boolean).join(' · '),
        by: o.reportedBy || by, date: o.reportedAt ? ymd(o.reportedAt) : today, uid: auth.currentUser?.uid || '', at: now,
        workOrder, job: opId, ...(o.launchCheck ? { fromCheck: { fix: o.launchCheck.fix, run: o.launchCheck.run } } : {}),
      };
    }
    if (o.extraPatch) Object.assign(patch, o.extraPatch({ workOrder, opId, dnuKey, by, now }));
    await update(ref(database), patch);
    dlg.close();
    o.toast?.(`${workOrder} ${target === 'new' ? 'created' : 'updated'}${pick ? ` · ${pick.tech} from ${shortDay(pick.start)}` : ''}${dnu ? ` · ${o.unitName || o.unitId} flagged do not use` : ''}`);
    o.onDone?.({ workOrder, opId });
  } catch (e) {
    console.error('[equipmentWork] work order failed', e);
    o.toast?.('Couldn’t create the work order — nothing was saved. Try again.', true);
  } finally {
    go.disabled = false;
  }
}

// ─── Taking a service from the techs app (docs/today-tasks-plan.md, More) ───────────────
// The service's open operation if there is one (Equipment_Service = its key), else a new
// work order like the dialog makes. Either way the person is added to Techs and, when the
// operation has no day yet, it's pinned to `date` (Locked), so it lands on their Today.

// Who changed the op's techs, so the person who did it gets no alert about it (functions/alerts.js).
const assignedBy = (opId, at) => ({ [`data/${opId}/Assigned_By`]: auth.currentUser?.uid || '', [`data/${opId}/Assigned_At`]: at });
const techList = v => String(v || '').split(',').map(x => x.trim()).filter(Boolean);
const isOpenOp = op => !CLOSED.includes(op.Status);

/** The open operation for one service on a unit, or null. */
export async function serviceOp(unitId, serviceKey) {
  return (await loadUnitOps(unitId)).find(op => op.Equipment_Service === serviceKey && isOpenOp(op)) || null;
}

/**
 * Put `techName` (the first name jobs are assigned by) on a unit's service for `date`.
 * service: { key, label }. Returns { opId, workOrder, created }.
 */
export async function takeServiceOp({ unitId, unitName, service, techName, date = todayYMD() }) {
  const now = Date.now();
  const existing = await serviceOp(unitId, service.key);
  if (existing) {
    const techs = techList(existing.Techs);
    const patch = { 'data/lastUpdated': now };
    if (!techs.includes(techName)) Object.assign(patch, { [`data/${existing.id}/Techs`]: [...techs, techName].join(', '), ...assignedBy(existing.id, now) });
    if (!existing.Schedule_Date) Object.assign(patch, { [`data/${existing.id}/Schedule_Date`]: date, [`data/${existing.id}/Locked`]: true });
    await update(ref(database), patch);
    return { opId: existing.id, workOrder: existing.Work_Order, created: false };
  }
  // Hours: what the last one of this service was estimated at, else an hour.
  const past = (await loadUnitOps(unitId)).filter(op => op.Equipment_Service === service.key && parseFloat(op.Estimated_Hours) > 0);
  const hours = past.length ? parseFloat(past[past.length - 1].Estimated_Hours) : 1;
  const workOrder = `WO-${(await runTransaction(ref(database, 'meta/nextWorkOrderSeq'), c => (c || 2200) + 1)).snapshot.val()}`;
  const opId = newKey(ref(database, 'data')).key;
  const by = actor();
  const op = {
    id: opId, Job_ID: opId, Work_Order: workOrder,
    Customer_ID: EQUIPMENT_CUSTOMER, Customer_Name: 'Equipment',
    Boat_ID: `equipment-${unitId}`, Boat_Name: unitName || unitId, Equipment_Unit: unitId,
    Category: INTERNAL_CATEGORY, Short_Description: service.label, Description: `Scheduled service, taken from the techs app by ${by}.`,
    Status: 'On Work Schedule', Priority: 'Normal', Start_Date: date, Request_Date: date, Charge: '0.00',
    Techs: techName, Schedule_Date: date, Locked: true, Equipment_Service: service.key, Estimated_Hours: String(hours),
    Assigned_By: auth.currentUser?.uid || '', Assigned_At: now,
  };
  const hKey = newKey(ref(database, `equipment/units/${unitId}/history`)).key;
  await update(ref(database), {
    [`data/${opId}`]: op, 'data/lastUpdated': now,
    [`equipment/units/${unitId}/history/${hKey}`]: { date, kind: 'note', text: `${workOrder}: ${service.label}`, meta: `${by} · from the techs app`, uid: auth.currentUser?.uid || '', at: now },
  });
  return { opId, workOrder, created: true };
}

/** Take `techName` off a service's operation (the operation itself stays). */
export async function leaveServiceOp(op, techName) {
  const techs = techList(op.Techs).filter(n => n !== techName);
  await update(ref(database), { [`data/${op.id}/Techs`]: techs.join(', '), ...assignedBy(op.id, Date.now()), 'data/lastUpdated': Date.now() });
}
